import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import i18n from "config/i18n/i18n";
import Settings from "config/settings";
import useStore from "config/store";
import useCompanyInitializer from "hooks/useCompanyInitializer";
import { CHECK_USER_AUTHENTICATED } from "queries/general";
import React, { createContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clearAccessToken, getAccessToken, setAccessToken } from "./authUtils";

export interface AuthContextType {
  loading: boolean;
  loginFromHash: () => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: loadingCompany } = useCompanyInitializer();

  const setUserData = useStore(state => state.setUserData);
  const resetStore = useStore(state => state.resetStore);
  const userData = useStore(state => state.userData);

  const [getUserData, { loading: loadingUser, error }] =
    useLazyQuery<IsAuthQueryResponseType>(CHECK_USER_AUTHENTICATED);

  useEffect(() => {
    if (error) onErrorGraphql(error);
  }, [error]);

  const fetchUser = async () => {
    const { data } = await getUserData();
    if (data?.getUserByToken) {
      Sentry.setUser({ email: data.getUserByToken.email, id: data.getUserByToken.id });
      setUserData({ ...data.getUserByToken });
      const lang = data.getUserByToken.language.toLowerCase();
      i18n.changeLanguage(lang);
      localStorage.setItem("language", lang);
    } else {
      logout();
    }
  };

  const loginFromHash = async () => {
    const hash = location.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.substring(1));
    const token = params.get("access_token");
    const expiresIn = Number(params.get("expires_in"));

    if (token && expiresIn) {
      setAccessToken(token, expiresIn);
      navigate(location.pathname, { replace: true });
      fetchUser();
    }
  };

  const logout = async () => {
    const token = getAccessToken();

    await axios(Settings.revokeToken, {
      method: "post",
      data: `token=${token}&client_id=${Settings.clientId}`,
    });

    clearAccessToken();
    resetStore();
    window.location.href = Settings.fullLoginUrl;
  };

  useEffect(() => {
    if (location.hash) {
      loginFromHash();
      return;
    }

    const token = getAccessToken();
    if (token && !userData) {
      fetchUser();
    } else if (!token) {
      window.location.href = Settings.fullLoginUrl;
    }
  }, []);

  const loading = loadingUser || loadingCompany;

  return (
    <AuthContext.Provider value={{ loading, loginFromHash, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
